import { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from "react";
import api, { mediaUrl } from "../lib/api";

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(new Audio());
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);

  const play = useCallback(async (episode) => {
    const audio = audioRef.current;
    if (current?.id === episode.id) {
      audio.play(); setPlaying(true); return;
    }
    audio.src = mediaUrl(episode.audio_url);
    setCurrent(episode);
    const saved = parseFloat(localStorage.getItem(`pos_${episode.id}`) || "0");
    audio.currentTime = saved || 0;
    audio.playbackRate = speed;
    try {
      await audio.play();
      setPlaying(true);
      api.post(`/episodes/${episode.id}/play`).catch((e) => console.debug("play tracking failed", e));
    } catch (error) {
      console.error("Audio playback error:", error);
      setPlaying(false);
    }
  }, [current, speed]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (audio.paused) { audio.play(); setPlaying(true); }
    else { audio.pause(); setPlaying(false); }
  }, []);

  const seek = useCallback((t) => { audioRef.current.currentTime = t; }, []);
  const skip = useCallback((s) => { audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime + s); }, []);
  const changeSpeed = useCallback((s) => { audioRef.current.playbackRate = s; setSpeed(s); }, []);

  useEffect(() => {
    const audio = audioRef.current;
    const onTime = () => {
      setProgress(audio.currentTime);
      if (current) {
        localStorage.setItem(`pos_${current.id}`, audio.currentTime);
        // Enforce preview limit for locked premium episodes
        if (current.locked && audio.currentTime >= (current.preview_seconds || 180)) {
          audio.pause(); setPlaying(false);
          audio.currentTime = current.preview_seconds || 180;
        }
      }
    };
    const onMeta = () => setDuration(audio.duration || 0);
    const onEnd = () => setPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
    };
  }, [current]);

  const value = useMemo(
    () => ({ current, playing, progress, duration, speed, play, toggle, seek, skip, changeSpeed }),
    [current, playing, progress, duration, speed, play, toggle, seek, skip, changeSpeed]
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};
